import type { ReactNode } from 'react'
import type { PerformanceSet } from '@/lib/market/types'

import { ChangeText } from '@/components/market/change'

interface PerformanceTableProps {
    ticker: string
    sectorName: string | null
    sectorId: string | null
    stock: PerformanceSet
    sector: PerformanceSet | null
    index: PerformanceSet
}

const PERIODS: Array<{ key: keyof PerformanceSet, label: string }> = [
    { key: 'w1', label: '1 week' },
    { key: 'm1', label: '1 month' },
    { key: 'm3', label: '3 months' },
    { key: 'm6', label: '6 months' },
    { key: 'ytd', label: 'Year to date' },
    { key: 'y1', label: '12 months' }
]

const PerformanceTable = ({ ticker, sectorName, sectorId, stock, sector, index }: PerformanceTableProps): ReactNode => {
    return (
        <div className='-mx-4 overflow-x-auto px-4 scroll-thin sm:mx-0 sm:px-0'>
            <table className='w-full min-w-[460px] text-left text-[13px]'>
                <caption className='sr-only'>{ticker} performance against its sector and the KASI</caption>
                <thead>
                    <tr className='border-b border-border text-[11px] uppercase tracking-[0.06em] text-muted-foreground'>
                        <th scope='col' className='py-2 pr-3 font-medium'>Period</th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>{ticker}</th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>
                            {sectorName && sectorId ? <span data-mdlfy-cms={`sectors:${sectorId}:name`}>{sectorName}</span> : 'Sector'}
                        </th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>KASI</th>
                        <th scope='col' className='py-2 text-right font-medium'>vs KASI</th>
                    </tr>
                </thead>
                <tbody>
                    {PERIODS.map(period => {
                        const own = stock[period.key]
                        const market = index[period.key]
                        const relative = own !== null && market !== null ? own - market : null

                        return (
                            <tr key={period.key} className='border-b border-border/60 last:border-0'>
                                <th scope='row' className='py-2 pr-3 font-normal text-muted-foreground'>{period.label}</th>
                                <td className='py-2 pr-3 text-right font-medium'><ChangeText value={own} /></td>
                                <td className='py-2 pr-3 text-right'><ChangeText value={sector ? sector[period.key] : null} /></td>
                                <td className='py-2 pr-3 text-right'><ChangeText value={market} /></td>
                                <td className='py-2 text-right'>
                                    <ChangeText value={relative} className='font-medium' />
                                    <span className='sr-only'> percentage points</span>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

export default PerformanceTable
